/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/shared/lib/utils";

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("rounded-xl border bg-card text-card-foreground shadow-sm", className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    chevronClassName?: string;
    isExpanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
  }
>(({ className, chevronClassName, isExpanded, onExpandedChange, children, ...props }, ref) => {
  const handleClick = onExpandedChange ? () => onExpandedChange(!isExpanded) : undefined;

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col space-y-1.5 p-6",
        onExpandedChange && "cursor-pointer transition-colors",
        className,
      )}
      onClick={handleClick}
      {...props}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex-1">{children}</div>
        {onExpandedChange && (
          <div className={cn("ml-4", chevronClassName)}>
            <ChevronDown
              className={cn(
                "h-6 w-6 transition-transform duration-200",
                isExpanded ? "rotate-180" : "",
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
});
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

// CollapsibleCard component
const CollapsibleCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    defaultExpanded?: boolean;
  }
>(({ className, defaultExpanded = false, children, ...props }, ref) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  return (
    <Card ref={ref} className={cn("overflow-hidden", className)} {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === CardHeader) {
          return React.cloneElement(child as React.ReactElement<any>, {
            isExpanded,
            onExpandedChange: setIsExpanded,
          });
        }
        if (React.isValidElement(child) && child.type === CardContent) {
          return isExpanded ? child : null;
        }
        return child;
      })}
    </Card>
  );
});
CollapsibleCard.displayName = "CollapsibleCard";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, CollapsibleCard };
